import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { History as HistoryIcon, Search, User, Wrench, Calendar, Trash2 } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ConfirmDialog } from '@/components/ConfirmDialog';

const History = () => {
  const { user } = useContext(AuthContext);
  const [history, setHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);

  useEffect(() => {
    loadHistory();
  }, []);

  useEffect(() => {
    const filtered = history.filter((item) =>
      item.collaborator?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.tool?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.tool?.type?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredHistory(filtered);
  }, [searchTerm, history]);

  const loadHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/assignments/history`);
      setHistory(data);
      setFilteredHistory(data);
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'historique');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      active: { label: 'Actif', variant: 'default' },
      returned: { label: 'Retourné', variant: 'secondary' },
      replaced: { label: 'Remplacé', variant: 'outline' },
      tool_deleted: { label: 'Outil supprimé', variant: 'destructive' },
    };
    const statusInfo = statusMap[status] || { label: status, variant: 'outline' };
    return <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>;
  };

  const handleClearHistory = () => {
    setConfirmClearOpen(true);
  };

  const confirmClearHistory = async () => {
    try {
      const { data } = await axios.delete(`${API}/assignments/history`);
      toast.success(data.message || 'Historique effacé avec succès');
      loadHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'effacement');
    } finally {
      setConfirmClearOpen(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg font-medium text-muted-foreground">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      <div>
        <h1 className="text-4xl font-heading font-bold tracking-tight leading-none">
          Historique des Affectations
        </h1>
        <p className="text-muted-foreground mt-2">
          Consultez l'historique complet de toutes les affectations
        </p>
      </div>

      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="history-search-input"
            placeholder="Rechercher dans l'historique..."
            className="pl-10 rounded-lg"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        {user?.role === 'admin' && (
          <Button
            data-testid="clear-history-button"
            variant="destructive"
            onClick={handleClearHistory}
            className="rounded-full shadow-lg"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Effacer l'historique
          </Button>
        )}
      </div>

      <div className="space-y-4">
        {filteredHistory.map((item) => (
          <Card
            key={item.id}
            data-testid={`history-item-${item.id}`}
            className="hover:shadow-lg transition-shadow duration-200"
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1 space-y-3">
                  <div className="flex items-center gap-4">
                    <Avatar>
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        {item.collaborator?.name?.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-muted-foreground" />
                        <span className="font-medium text-lg">{item.collaborator?.name}</span>
                      </div>
                      {item.collaborator?.email && (
                        <p className="text-sm text-muted-foreground">{item.collaborator.email}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 ml-12">
                    <Wrench className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium">{item.tool?.name}</span>
                    <span className="text-sm text-muted-foreground">({item.tool?.type})</span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 ml-12 text-sm">
                    <div>
                      <span className="text-muted-foreground">Affecté le:</span>
                      <p className="font-medium">{formatDate(item.assigned_date)}</p>
                    </div>
                    {item.end_date && (
                      <div>
                        <span className="text-muted-foreground">Fin prévue:</span>
                        <p className="font-medium">{formatDate(item.end_date)}</p>
                      </div>
                    )}
                    {item.returned_date && (
                      <div>
                        <span className="text-muted-foreground">Retourné le:</span>
                        <p className="font-medium">{formatDate(item.returned_date)}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div data-testid={`status-badge-${item.id}`}>
                  {getStatusBadge(item.status)}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredHistory.length === 0 && (
        <div className="text-center py-12">
          <HistoryIcon className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Aucun historique trouvé</p>
        </div>
      )}

      <ConfirmDialog
        open={confirmClearOpen}
        onOpenChange={setConfirmClearOpen}
        onConfirm={confirmClearHistory}
        title="Effacer l'historique"
        description="Êtes-vous sûr de vouloir effacer tout l'historique des affectations terminées ? Les affectations actives ne seront pas supprimées. Cette action est irréversible."
        confirmText="Effacer"
        cancelText="Annuler"
      />
    </div>
  );
};

export default History;